import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import CustomTypography from "../../globalStyles/fonts";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { countryService } from "../../utils/services/country.service";
import { provinceService } from "../../utils/services/province.service";

const ProvinceModal = ({ open, handleClose, onAddData, selectedProvince }) => {
  const [provinceDetails, setProvinceDetails] = useState({
    country: null,
    code: "",
    name: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);

  const resetForm = () => {
    setProvinceDetails({ country: null, code: "", name: "" });
    setSelectedCountry(null);
    setFormErrors({});
  };

  const validate = () => {
    const err = {};
    if (!provinceDetails.country) err.country = "Country is required.";
    if (!provinceDetails.code.trim()) err.code = "Code is required.";
    if (!provinceDetails.name.trim()) err.name = "Name is required.";
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  useEffect(() => {
    if (selectedProvince) {
      setProvinceDetails({
        country: selectedProvince.country || null,
        code: selectedProvince.code || "",
        name: selectedProvince.name || "",
      });
      setSelectedCountry(selectedProvince.country || null);
    } else {
      resetForm();
    }
  }, [selectedProvince]);

  const handleInput = ({ target: { name, value } }) =>
    setProvinceDetails((p) => ({ ...p, [name]: value }));

  const save = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await provinceService.create({
        ...provinceDetails,
        country: provinceDetails.country?._id,
      });
      onAddData?.();
      toast.success("Province created successfully");
      resetForm();
      handleClose();
    } catch {
      toast.error("Failed to create province");
    }
  };

  const update = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await provinceService.edit(
        {
          country: provinceDetails.country?._id,
          code: provinceDetails.code,
          name: provinceDetails.name,
        },
        selectedProvince._id,
      );
      onAddData?.();
      toast.success("Province updated successfully");
      resetForm();
      handleClose();
    } catch {
      toast.error("Failed to update province");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        resetForm();
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle
          style={{ fontFamily: "Poppins", fontSize: 24, fontWeight: 500 }}
        >
          {selectedProvince ? "Edit Province" : "Create Province"}
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 10 }} />

      <DialogContent>
        <Grid container spacing={2}>
          {/* Country autocomplete */}
          <Grid item xs={12}>
            <CustomTypography
              fontSize={14}
              fontWeight={400}
              color="var(--text-heading)"
            >
              Country
            </CustomTypography>
            <AutocompleteWithPagination
              backgroundColor="var(--bg-paper)"
              customLabel="Country"
              selectedItem={selectedCountry}
              onDataChange={(country) => {
                setSelectedCountry(country);
                setProvinceDetails((p) => ({ ...p, country }));
              }}
              width="100%"
              optionKey="name"
              apiEndPoint={(limit, skip, search = "") => {
                const query = `$limit=${limit}&$skip=${skip}`;
                return countryService.fetch(search, query, "name");
              }}
              errorMessage={formErrors.country}
            />
          </Grid>

          {/* Code field */}
          <Grid item xs={12} md={6}>
            <CustomTypography
              fontSize={14}
              fontWeight={400}
              color="var(--text-heading)"
            >
              Code
            </CustomTypography>
            <TextField
              size="small"
              name="code"
              value={provinceDetails.code}
              onChange={handleInput}
              fullWidth
              margin="dense"
              inputProps={{ maxLength: 10 }}
              error={!!formErrors.code}
              helperText={formErrors.code}
            />
          </Grid>

          {/* Name field */}
          <Grid item xs={12} md={6}>
            <CustomTypography
              fontSize={14}
              fontWeight={400}
              color="var(--text-heading)"
            >
              Name
            </CustomTypography>
            <TextField
              size="small"
              name="name"
              value={provinceDetails.name}
              onChange={handleInput}
              fullWidth
              margin="dense"
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: 12 }}>
        <Button
          onClick={() => {
            handleClose();
            resetForm();
          }}
          variant="outlined"
          style={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={selectedProvince ? update : save}
          variant="contained"
          style={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 600 }}
        >
          {selectedProvince ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProvinceModal;
